import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../features/auth/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = []
}) => {
  const authState = useAuthStore();
  const { isAuthenticated, user, isLoading } = authState;
  const location = useLocation();

  // Log the raw auth state
  console.log('🔍 Raw auth state from useAuthStore:', authState);
  console.log('🔍 Raw localStorage data:', localStorage.getItem('erpca-auth'));

  console.log('🔍 ProtectedRoute Debug:', {
    path: location.pathname,
    isAuthenticated,
    isLoading,
    user: user ? { email: user.email, roles: user.roles } : null,
    requiredRoles,
    userRolesType: user?.roles ? typeof user.roles : 'undefined',
    userRolesArray: Array.isArray(user?.roles),
  });

  // Show loading state while authentication is being initialized
  if (isLoading) {
    console.log('⏳ Authentication loading, showing loading state');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    console.log('❌ Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If specific roles are required, check if user has them
  if (requiredRoles.length > 0) {
    console.log('🔑 Checking roles:', {
      required: requiredRoles,
      userRoles: user.roles,
      userRolesType: typeof user.roles,
      isArray: Array.isArray(user.roles)
    });

    // Ensure user.roles is an array and normalize case
    const userRoles = Array.isArray(user.roles) ? user.roles : [];
    const normalizedUserRoles = userRoles.map(role => String(role).trim());
    const normalizedRequiredRoles = requiredRoles.map(role => String(role).trim());

    console.log('🔄 Normalized roles:', {
      userRoles: normalizedUserRoles,
      requiredRoles: normalizedRequiredRoles
    });

    const hasRequiredRole = normalizedRequiredRoles.some(role => {
      const hasRole = normalizedUserRoles.includes(role);
      console.log(`- Role "${role}": ${hasRole} (checking in [${normalizedUserRoles.join(', ')}])`);
      return hasRole;
    });

    console.log('🎯 Final role check result:', hasRequiredRole);

    // Additional fallback check for common role variations
    let finalAccessDecision = hasRequiredRole;

    // Handle common role variations (FirmAdmin vs FIRM_ADMIN)
    if (!finalAccessDecision && normalizedRequiredRoles.length > 0) {
      const roleVariations = normalizedRequiredRoles.flatMap(role => {
        if (role === 'FIRM_ADMIN') return ['FirmAdmin', 'FIRM_ADMIN'];
        if (role === 'FirmAdmin') return ['FirmAdmin', 'FIRM_ADMIN'];
        if (role === 'SUPER_ADMIN') return ['SuperAdmin', 'SUPER_ADMIN'];
        if (role === 'SuperAdmin') return ['SuperAdmin', 'SUPER_ADMIN'];
        return [role];
      });

      finalAccessDecision = roleVariations.some(role => normalizedUserRoles.includes(role));
    }

    if (!hasRequiredRole) {
      const fallbackCheck = normalizedRequiredRoles.some(reqRole => {
        return normalizedUserRoles.some(userRole => {
          // Check exact match (already done above)
          if (userRole === reqRole) return true;

          // Check case-insensitive match
          if (userRole.toLowerCase() === reqRole.toLowerCase()) return true;

          // Check common variations
          const variations = [
            reqRole.replace(/([A-Z])/g, '_$1').toUpperCase().substring(1), // SuperAdmin -> SUPER_ADMIN
            reqRole.replace(/([A-Z])/g, ' $1').trim(), // SuperAdmin -> Super Admin
            reqRole.toLowerCase(), // SuperAdmin -> superadmin
            reqRole.toUpperCase(), // SuperAdmin -> SUPERADMIN
          ];

          return variations.some(variation => userRole === variation || userRole.toLowerCase() === variation.toLowerCase());
        });
      });

      console.log('🔄 Fallback role check result:', fallbackCheck);
      finalAccessDecision = fallbackCheck;

      if (fallbackCheck) {
        console.log('✅ Access granted via fallback role check');
      }
    }

    if (!finalAccessDecision) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-soft p-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Access Denied
              </h2>
              <p className="text-gray-600 mb-4">
                You don't have permission to access this page.
              </p>
              <p className="text-sm text-gray-500">
                Required roles: {requiredRoles.join(', ')}
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
